import { MessageFormatElement } from '@formatjs/icu-messageformat-parser';
import {
    LocalizationProvider as MuiLocalizationProvider,
    AdapterDateFnsV3,
} from '@evaneos/b2b-ui/date-picker';
import { Locale as DateFnsLocale, setDefaultOptions } from 'date-fns';
import { createContext, useContext, useState, useEffect, PropsWithChildren } from 'react';
import { IntlProvider } from 'react-intl';

import { getCookieFromName } from '../utilities/cookies';
import { checkSupportedLocales, setLocaleInCookie, getTranslations } from '../intl/operations';
import { AvailableLocale } from '../intl/types';

interface LocaleContextValue {
    locale: AvailableLocale;
    setLocale: (locale: AvailableLocale) => void;
}

export const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export const LocalizationProvider = ({ children }: PropsWithChildren) => {
    const [locale, setLocaleState] = useState<AvailableLocale>(() => {
        return checkSupportedLocales(getCookieFromName('request-manager_locale')) || 'en';
    });
    const [dateFnsLocale, setDateFnsLocale] = useState<DateFnsLocale>();

    const setLocale = (newLocale: AvailableLocale) => {
        setLocaleState(newLocale);
        setLocaleInCookie(newLocale);
    };

    const { code, messages } = getTranslations(locale);

    // Load date-fns locale and set it as the global default so every
    // date-fns call across the app (format, formatDistance…) is localized
    useEffect(() => {
        void loadDateFnsLocale(locale).then((dateFnsLocale) => {
            setDateFnsLocale(dateFnsLocale);
            setDefaultOptions({ locale: dateFnsLocale });
        });
    }, [locale]);

    // Set document language
    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.lang = code;
        }
    }, [code]);

    return (
        <LocaleContext.Provider value={{ locale, setLocale }}>
            <IntlProvider
                key={locale}
                locale={code}
                messages={
                    messages as Record<string, string> | Record<string, MessageFormatElement[]>
                }
            >
                <MuiLocalizationProvider
                    dateAdapter={AdapterDateFnsV3}
                    adapterLocale={dateFnsLocale}
                >
                    {children}
                </MuiLocalizationProvider>
            </IntlProvider>
        </LocaleContext.Provider>
    );
};

export const useLocale = (): LocaleContextValue => {
    const context = useContext(LocaleContext);
    if (!context) {
        throw new Error('useLocale must be used within a LocalizationProvider');
    }
    return context;
};

const loadDateFnsLocale = async (locale: string): Promise<DateFnsLocale> => {
    switch (locale) {
        case 'en':
            return (await import('date-fns/locale/en-US')).enUS;
        case 'fr':
            return (await import('date-fns/locale/fr')).fr;
        case 'it':
            return (await import('date-fns/locale/it')).it;
        case 'es':
            return (await import('date-fns/locale/es')).es;
        case 'de':
            return (await import('date-fns/locale/de')).de;
        case 'sv':
            return (await import('date-fns/locale/sv')).sv;
        case 'nl':
            return (await import('date-fns/locale/nl')).nl;
        default:
            return (await import('date-fns/locale/en-US')).enUS;
    }
};
